import { Point, Rect } from 'dragonmouse-shared/geometry';
import { SceneComment } from 'dragonmouse-shared/interfaces';
import {
	centerCanvasAroundSceneBoundingBox,
	setActiveCommentId,
	setActiveSceneChatId,
} from 'redux/mode/actions';
import eventManager from 'events/EventManager';
import store from 'redux/store';
import { Store, ZoomType, selectTransform } from 'redux/mode/reducers';

export const getScenePoint = (canvas: Point): Point => {
	const transform = selectTransform(store.getState().mode);
	return {
		x: (canvas.x - transform.offset.x) / transform.scale,
		y: (canvas.y - transform.offset.y) / transform.scale,
	};
};

export const getCanvasPoint = (scene: Point): Point => {
	const transform = selectTransform(store.getState().mode);
	return {
		x: transform.scale * scene.x + transform.offset.x,
		y: transform.scale * scene.y + transform.offset.y,
	};
};

export const getSceneRect = (canvas: Rect): Rect => {
	const topleft: Point = getScenePoint({ x: canvas.x, y: canvas.y });
	const bottomright: Point = getScenePoint({
		x: canvas.x + canvas.width,
		y: canvas.y + canvas.height,
	});

	return {
		x: topleft.x,
		y: topleft.y,
		width: bottomright.x - topleft.x,
		height: bottomright.y - topleft.y,
	};
};

export const getCanvasRect = (scene: Rect): Rect => {
	const topleft: Point = getCanvasPoint({ x: scene.x, y: scene.y });
	const bottomright: Point = getCanvasPoint({
		x: scene.x + scene.width,
		y: scene.y + scene.height,
	});

	return {
		x: topleft.x,
		y: topleft.y,
		width: bottomright.x - topleft.x,
		height: bottomright.y - topleft.y,
	};
};

export const getCanvasPointForEvent = (e: any): Point => {
	const rect = e.target.getBoundingClientRect();
	return {
		x: e.clientX - rect.left,
		y: e.clientY - rect.top,
	};
};

export const getVisibleCanvasCenterInSceneCoordinates = (): Point => {
	const state = store.getState().mode;

	const canvasCenter: Point = {
		x: openCanvasLeft(state) + openCanvasWidth(state) / 2,
		y: openCanvasHeight(state) / 2,
	};

	return getScenePoint(canvasCenter);
};

export const getScenePointForEvent = (e: any): Point => {
	const canvas = getCanvasPointForEvent(e);
	return getScenePoint(canvas);
};

export const MAX_SCALE = 6;
export const MIN_SCALE = 0.02;

export const updateScale = (state: Store, zoomType: ZoomType): number => {
	const transform = selectTransform(state);

	switch (zoomType) {
		case ZoomType.ZOOM_IN:
			return Math.min(transform.scale * 2, MAX_SCALE);
		case ZoomType.ZOOM_OUT:
			return Math.max(transform.scale / 2, MIN_SCALE);
		default:
			return transform.scale;
	}
};

export const openCanvasWidth = (state: Store) => {
	const { canvasWidth, panelsVisible, leftPanelWidths, activeLeftPanelTab, rightPanelWidth } =
		state;
	if (canvasWidth === null) return 0;

	const leftWidth = panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
	const rightWidth = panelsVisible ? rightPanelWidth : 0;

	return canvasWidth - (leftWidth + rightWidth);
};

export const openCanvasLeft = (state: Store): number => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = state;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};

export const openCanvasHeight = (state: Store): number => {
	const { canvasHeight } = state;
	const toolIslandHeightAndPadding = 60;
	if (canvasHeight === null) return 0;
	return canvasHeight - toolIslandHeightAndPadding;
};

export const getOffsetToPreservePositionWhenScaling = (
	state: Store,
	canvasPosition: Point,
	scale: number
): Point => {
	const transform = selectTransform(state);

	const scenePositionBeforeScaling = {
		x: (canvasPosition.x - transform.offset.x) / transform.scale,
		y: (canvasPosition.y - transform.offset.y) / transform.scale,
	};

	const canvasPositionAfterScaling = {
		x: scale * scenePositionBeforeScaling.x + transform.offset.x,
		y: scale * scenePositionBeforeScaling.y + transform.offset.y,
	};

	const delta = {
		x: canvasPosition.x - canvasPositionAfterScaling.x,
		y: canvasPosition.y - canvasPositionAfterScaling.y,
	};

	return {
		x: transform.offset.x + delta.x,
		y: transform.offset.y + delta.y,
	} as Point;
};

export const shiftXOffset = (state: Store, deltaX: number): Point => {
	const transform = selectTransform(state);
	return {
		x: transform.offset.x + deltaX,
		y: transform.offset.y,
	};
};

export const shiftYOffset = (state: Store, deltaY: number): Point => {
	const transform = selectTransform(state);
	return {
		x: transform.offset.x,
		y: transform.offset.y + deltaY,
	};
};

export const canvasOffsetX = () => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = store.getState().mode;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};

// Zoom to a comment and restore its chat state. Used by SelectTool and prev/next navigation.
export const navigateToComment = (comment: SceneComment) => {
	const modeState = store.getState().mode;
	const currentScale = selectTransform(modeState).scale;
	const commentZoom = comment.metadata?.zoom || currentScale;
	const padding = 1.1;
	const w = openCanvasWidth(modeState) / (commentZoom * padding);
	const h = openCanvasHeight(modeState) / (commentZoom * padding);

	store.dispatch(setActiveCommentId(comment.id));
	store.dispatch(
		centerCanvasAroundSceneBoundingBox({
			boundingBox: {
				x: comment.x - w / 2,
				y: comment.y - h / 2,
				width: w,
				height: h,
			},
			animated: true,
		})
	);

	// Restore chat state if present
	if (comment.metadata?.chatId) {
		if (comment.metadata.chatId !== modeState.activeSceneChatId) {
			store.dispatch(setActiveSceneChatId(comment.metadata.chatId));
		}
		if (comment.metadata.chatScrollTop != null && comment.metadata.chatScrollHeight != null) {
			eventManager.emit('scroll-chat-to-position', {
				scrollTop: comment.metadata.chatScrollTop,
				scrollHeight: comment.metadata.chatScrollHeight,
			});
		}
	}
};
